import parseISO from 'date-fns/parseISO';
import toString from 'lodash/toString';
import { useIntl } from 'react-intl';

import type { Schema } from '@strapi/types';

interface CellValueProps {
  isIdColumn?: boolean;
  type: Schema.Attribute.Kind | 'custom';
  value: unknown;
}

const CellValue = ({ isIdColumn = false, type, value }: CellValueProps) => {
  const { formatDate, formatTime, formatNumber, formatMessage } = useIntl();
  let formattedValue = value;

  if (type === 'date' && typeof value === 'string') {
    formattedValue = formatDate(parseISO(value), { dateStyle: 'full' });
  }

  if (type === 'datetime') {
    formattedValue = formatDate(value as Parameters<typeof formatDate>[0], {
      dateStyle: 'full',
      timeStyle: 'short',
    });
  }

  if (type === 'time' && typeof value === 'string') {
    const [hour, minute, second] = value.split(':');
    const date = new Date();
    date.setHours(Number(hour));
    date.setMinutes(Number(minute));
    date.setSeconds(Number(second));

    formattedValue = formatTime(date, {
      timeStyle: 'short',
    });
  }

  if (['float', 'decimal'].includes(type)) {
    formattedValue = formatNumber(value as Parameters<typeof formatNumber>[0], {
      // Should be kept in sync with the corresponding value
      // in the design-system/NumberInput: https://github.com/strapi/design-system/blob/main/packages/strapi-design-system/src/NumberInput/NumberInput.js#L53
      maximumFractionDigits: 20,
    });
  }

  if (type === 'boolean') {
    const booleanValue = Boolean(value);
    formattedValue = formatMessage({
      id: booleanValue
        ? 'app.components.ToggleCheckbox.on-label'
        : 'app.components.ToggleCheckbox.off-label',
      defaultMessage: booleanValue ? 'true' : 'false',
    });
  }

  if (['integer', 'biginteger'].includes(type)) {
    formattedValue = formatNumber(value as Parameters<typeof formatNumber>[0], {
      maximumFractionDigits: 0,
      useGrouping: !isIdColumn,
    });
  }

  return toString(formattedValue);
};

export { CellValue };
export type { CellValueProps };
